// Mise en forme des chiffres et des périodes du volet statistiques.

const MONTHS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const nf = (digits) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const formatNumber = (value, digits = 0) =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : nf(digits).format(value);

/** Valeur d'une mesure, avec son unité : « 12 500 DA », « 8,4 % », « 3,2 jours ». */
export const formatMeasure = (value, unit) => {
  if (value === null || value === undefined) return '—';
  if (unit === 'DA') return `${formatNumber(value, 0)} DA`;
  if (unit === '%') return `${formatNumber(value, 1)} %`;
  if (unit === 'jours') return `${formatNumber(value, 1)} j`;
  return formatNumber(value, Number.isInteger(value) ? 0 : 1);
};

/** Grand nombre abrégé pour les axes de graphique : 1,2 M ; 850 k. */
export const formatCompact = (value) => {
  if (value === null || value === undefined) return '';
  const abs = Math.abs(value);
  if (abs >= 1e6) return `${formatNumber(value / 1e6, 1)} M`;
  if (abs >= 1e3) return `${formatNumber(value / 1e3, abs >= 1e4 ? 0 : 1)} k`;
  return formatNumber(value, 0);
};

/** Clé de période du serveur → libellé lisible. Toute autre valeur est rendue telle quelle. */
export const formatPeriod = (key) => {
  const s = String(key ?? '');
  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m) return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  m = /^(\d{4})-T(\d)$/.exec(s);
  if (m) return `T${m[2]} ${m[1]}`;
  m = /^(\d{4})-S(\d{2})$/.exec(s);
  if (m) return `Sem. ${Number(m[2])} ${m[1]}`;
  return s;
};

const day = (d) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};
const endOfDay = (d) => {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
};

/** Périodes proposées ; `range()` renvoie { from, to } ou des nulls pour « tout ». */
export const PERIOD_PRESETS = [
  { key: '30j', label: '30 jours', range: () => ({ from: day(Date.now() - 29 * 864e5), to: endOfDay(new Date()) }) },
  { key: '90j', label: '90 jours', range: () => ({ from: day(Date.now() - 89 * 864e5), to: endOfDay(new Date()) }) },
  {
    key: '12m',
    label: '12 mois',
    range: () => {
      const from = new Date();
      from.setFullYear(from.getFullYear() - 1);
      from.setDate(from.getDate() + 1);
      return { from: day(from), to: endOfDay(new Date()) };
    },
  },
  { key: 'annee', label: 'Année en cours', range: () => ({ from: new Date(new Date().getFullYear(), 0, 1), to: endOfDay(new Date()) }) },
  { key: 'tout', label: 'Tout', range: () => ({ from: null, to: null }) },
];

/** Date → valeur d'un <input type="date">, en heure locale. */
export const toDateInput = (d) => {
  if (!d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const fromDateInput = (value, end = false) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d);
};

export const PERIMETER_LABELS = {
  all: 'Toute l\'entreprise',
  commercial: 'Demandes arrivées au service commercial',
  zone: 'Votre zone',
  self: 'Vos demandes',
  depot: 'Votre dépôt',
};

// Palette des graphiques (validée pour le daltonisme) : une teinte pour les
// grandeurs, quatre couleurs d'état réservées aux statuts.
export const CHART = {
  series: '#2a78d6',
  seriesSoft: '#cde2fb',
  track: '#eef1f4',
  grid: '#e6e8eb',
  ink: '#0b0b0b',
  inkSecondary: '#52514e',
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
  // Rampe ordinale (du plus récent au plus ancien) pour l'ancienneté des demandes en attente.
  age: ['#86b6ef', '#3987e5', '#1c5cab', '#0d366b'],
};
